import React, { useEffect, useMemo, useState } from "react";
import { Table, Skeleton } from "antd";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteABooking,
  getBookings,
  updateABooking,
  markBookingAsRead,
} from "../features/booking/bookingSlice";
import { AiFillDelete, AiOutlineEye } from "react-icons/ai";
import { Link } from "react-router-dom";
import CustomModal from "../components/CustomModal";

const WaterRefillBookings = () => {
  const dispatch = useDispatch();

  const [open, setOpen] = useState(false);
  const [bookingId, setBookingId] = useState("");
  const [selectedWeek, setSelectedWeek] = useState("all");
  const [selectedMonth, setSelectedMonth] = useState("all");

  useEffect(() => {
    dispatch(getBookings());
  }, [dispatch]);

  const { bookings = [], isLoading } = useSelector((state) => state.booking);

  const showSkeleton = isLoading && bookings.length === 0;

  const waterBookings = useMemo(() => {
    return bookings.filter(
      (booking) =>
        booking.serviceType === "water" ||
        booking.serviceType === "water-refill"
    );
  }, [bookings]);

  const getWeekOfMonth = (date) => {
    const currentDate = new Date(date);
    const day = currentDate.getDate();

    if (day <= 7) return "week-1";
    if (day <= 14) return "week-2";
    if (day <= 21) return "week-3";
    return "week-4";
  };

  const filteredBookings = useMemo(() => {
    return waterBookings.filter((booking) => {
      const bookingDate = booking.createdAt
        ? new Date(booking.createdAt)
        : booking.preferredDate
        ? new Date(booking.preferredDate)
        : null;

      if (!bookingDate) return true;

      const bookingMonth = bookingDate.getMonth() + 1;
      const bookingWeek = getWeekOfMonth(bookingDate);

      const matchMonth =
        selectedMonth === "all" || Number(selectedMonth) === bookingMonth;

      const matchWeek = selectedWeek === "all" || selectedWeek === bookingWeek;

      return matchMonth && matchWeek;
    });
  }, [waterBookings, selectedMonth, selectedWeek]);

  const stats = useMemo(() => {
    return {
      total: filteredBookings.length,
      newOrders: filteredBookings.filter((b) => b.status === "New").length,
      progress: filteredBookings.filter(
        (b) => b.status === "Submitted" || b.status === "Contacted"
      ).length,
      cancelled: filteredBookings.filter((b) => b.status === "Cancelled").length,
      completed: filteredBookings.filter((b) => b.status === "Completed").length,
    };
  }, [filteredBookings]);

  const showModal = (id) => {
    setOpen(true);
    setBookingId(id);
  };

  const hideModal = () => {
    setOpen(false);
  };

  const handleReadBookingClick = (id) => {
    dispatch(markBookingAsRead({ id }));
  };

  const setBookingStatus = async (status, id) => {
    await dispatch(updateABooking({ id, bookingData: status }));
    dispatch(getBookings());
  };

  const deleteBooking = async (id) => {
    await dispatch(deleteABooking(id));
    setOpen(false);
    dispatch(getBookings());
  };

  const columns = [
    { title: "SNo", dataIndex: "key", width: 60 },
    { title: "Name", dataIndex: "name", width: 160 },
    { title: "Email", dataIndex: "email", width: 220 },
    { title: "Mobile", dataIndex: "mobile", width: 150 },
    { title: "Litres", dataIndex: "litres", width: 100 },
    { title: "Containers", dataIndex: "numberOfContainers", width: 130 },
    { title: "Estate", dataIndex: "apartment", width: 150 },
    { title: "House", dataIndex: "houseNumber", width: 130 },
    { title: "Date", dataIndex: "preferredDate", width: 130 },
    { title: "Time", dataIndex: "preferredTime", width: 110 },
    { title: "Price", dataIndex: "price", width: 130 },
    { title: "Status", dataIndex: "status", width: 160 },
    { title: "Action", dataIndex: "action", width: 140 },
  ];

  const generateData = (bookings) =>
    bookings.map((booking, index) => ({
      key: index + 1,
      name: booking.name,
      email: booking.email || "N/A",
      mobile: booking.phone,
      litres: booking.litres ? `${booking.litres} L` : "N/A",
      numberOfContainers: booking.numberOfContainers || 0,
      apartment: booking.apartment || "N/A",
      houseNumber: booking.houseNumber || "N/A",
      preferredDate: booking.preferredDate
        ? new Date(booking.preferredDate).toLocaleDateString()
        : "N/A",
      preferredTime: booking.preferredTime || "N/A",
      price: `KSh ${Number(booking.price || 0).toLocaleString()}`,

      status: (
        <select
          value={booking.status || "New"}
          className="booking-status-select"
          onChange={(e) => setBookingStatus(e.target.value, booking._id)}
        >
          <option value="New">New</option>
          <option value="Contacted">Contacted</option>
          <option value="Completed">Completed</option>
          <option value="Cancelled">Cancelled</option>
        </select>
      ),

      action: (
        <div className="booking-actions">
          <Link
            className="booking-action-link"
            to={`/admin/water-refill/${booking._id}`}
            onClick={() => handleReadBookingClick(booking._id)}
          >
            <AiOutlineEye />
          </Link>

          <button
            className="booking-action-delete"
            onClick={() => showModal(booking._id)}
          >
            <AiFillDelete />
          </button>
        </div>
      ),

      className: booking.read ? "read-row" : "unviewed-row",
    }));

  return (
    <div>
      <div className="booking-page-header">
        <div>
          <h3 className="title">Water Refill Bookings</h3>
          <p>Manage water refill orders and customer delivery schedules.</p>
        </div>

        <div className="booking-filter-bar">
          <div className="filter-group">
            <select
              value={selectedWeek}
              onChange={(e) => setSelectedWeek(e.target.value)}
            >
              <option value="all">All Weeks</option>
              <option value="week-1">Week 1</option>
              <option value="week-2">Week 2</option>
              <option value="week-3">Week 3</option>
              <option value="week-4">Week 4</option>
            </select>
          </div>

          <div className="filter-group">
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
            >
              <option value="all">All Months</option>
              <option value="1">January</option>
              <option value="2">February</option>
              <option value="3">March</option>
              <option value="4">April</option>
              <option value="5">May</option>
              <option value="6">June</option>
              <option value="7">July</option>
              <option value="8">August</option>
              <option value="9">September</option>
              <option value="10">October</option>
              <option value="11">November</option>
              <option value="12">December</option>
            </select>
          </div>
        </div>
      </div>

      <div className="booking-stats-grid">
        <div className="booking-stat-card">
          <span>Total Orders</span>
          <h4>{stats.total}</h4>
        </div>

        <div className="booking-stat-card">
          <span>New Orders</span>
          <h4>{stats.newOrders}</h4>
        </div>

        <div className="booking-stat-card">
          <span>Progress Orders</span>
          <h4>{stats.progress}</h4>
        </div>

        <div className="booking-stat-card">
          <span>Cancelled Orders</span>
          <h4>{stats.cancelled}</h4>
        </div>

        <div className="booking-stat-card">
          <span>Completed Orders</span>
          <h4>{stats.completed}</h4>
        </div>
      </div>

      {showSkeleton ? (
        <div className="booking-table-skeleton">
          <Skeleton active paragraph={{ rows: 10 }} />
        </div>
      ) : (
        <div className="bookings-table-card">
          <Table
            columns={columns}
            dataSource={generateData(filteredBookings)}
            rowClassName={(record) => record.className}
            scroll={{ x: 2200 }}
            loading={isLoading && bookings.length > 0}
          />
        </div>
      )}

      <CustomModal
        hideModal={hideModal}
        open={open}
        performAction={() => deleteBooking(bookingId)}
        title="Are you sure you want to delete this booking?"
      />
    </div>
  );
};

export default WaterRefillBookings;